import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { getToolTableColumns } from "../../pages/tools/constants";
import { ToolService } from "../../services";
import { DELETE_TOOL, EDIT_TOOL, EXPORT_TOOL } from "../../constants";
import { AddEditTool } from "./AddEditTool.component";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ToolsList = ({
  companyId = null,
  totalCount = 0,
  setTotalCount = () => {},
  storeId,
  onSuccess = () => {},
  reloadData,
}) => {
  const [tools, setTools] = useState([]);
  const [toolId, setToolId] = useState(null);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "tools",
    defaults: { sortModel: [{ field: "updatedAt", sort: "desc" }] },
  });
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    toolId: null,
  });

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEditTool = useHasPermission(EDIT_TOOL);
  const hasDeleteTool = useHasPermission(DELETE_TOOL);
  const hasToolExport = useHasPermission(EXPORT_TOOL);

  const fetchData = useCallback(
    async (toolsFilters = {}) => {
      setLoading(true);
      try {
        const { data: toolsResponse } = await ToolService.getTools({ ...toolsFilters, ...(companyId !== null && { companyId }) });
        setTools(toolsResponse.rows);
        setTotalCount(toolsResponse.count);
      } catch (error) {
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, searchText, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(getToolTableColumns());
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Tools" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Tools",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/tools/${params.row.id}`);
  };

  const handleOpenModal = (toolData) => {
    setToolId(toolData?.id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setToolId(null);
    setOpenModal(false);
  };

  const openDeleteModal = (expense) => {
    setDeleteModal({
      isOpen: true,
      toolId: expense?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      toolId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await ToolService.deleteTool(deleteModal.toolId);
      toast.success("Tool deleted successfully");
      closeDeleteModal();
      onSuccess?.();
    } catch (error) {
      toast.error("Failed to delete tool");
      console.error("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const handleActions = (toolData, actionType) => {
    switch (actionType) {
      case "edit":
        handleOpenModal(toolData);
        break;
      case "delete":
        openDeleteModal(toolData);
        break;
      default:
        break;
    }
  };

  const columnHeaders = getToolTableColumns(handleActions, hasEditTool, hasDeleteTool);

  const rows = tools
    ? tools.map((tool, index) => ({
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        id: tool?.id,
        name: tool?.name,
        description: tool?.description,
        code: tool?.code,
        totalQuantity: tool.totalQuantity,
      }))
    : [];
  return (
    <>
      <CardLayout>
        <MTable
          // search data
          placeholder="Search tools"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Export data
          showExportButton={hasToolExport}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>

      {/* edit tool modal */}
      <AddEditTool open={openModal} toolId={toolId} storeId={storeId} onClose={handleCloseModal} onSuccess={onSuccess} />

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this tool?"
      />
    </>
  );
};
